import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useStoredUserData } from '../authentication/userSession';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IELTSResults from './IELTSResults';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Home/Footer';
import BrowserNotSupported from './BrowserNotSupported';
import TestCard from '../components/IELTS/TestCard';
import SectionalTestCard from '../components/IELTS/SectionalTestCard';
import CommunityChat from '../components/IELTS/CommunityChat';
import IeltsDashboardTour from '../components/IELTS/IeltsDashboardTour';
import {
  findFullIeltsDraftSummaryFromBrowser,
  removeFullIeltsDraftsFromBrowser,
  buildIeltsDraftKey,
  loadIeltsDraftFromBrowser,
} from '../components/IELTS/utils/ieltsDraftStorage';
import {
  CalculatorIcon,
  CalendarIcon,
  ListeningIcon,
  ReadingIcon,
  SpeakingIcon,
  TargetIcon,
  WritingIcon,
} from '../components/IELTS/icons';
import { normalizeIeltsTestKind, sections, testFilters } from '../components/IELTS/mockData';
import { Base_URL } from '../Client/apiURL';
import { IELTS_SECTION_DURATIONS } from './ieltsFullTestSections';
import { themeColors } from '../components/IELTS/styles/theme';
import { formatIELTSBandScore } from '../components/IELTS/utils/scoreHelpers';
import { buildMockTestPath } from '../utils/mockTestLinks';
import './IELTSDashboardV2.css';
import studyPlannerImage from '../assets/images/ielts-study-planner.jpg';

const isNotChrome = () => {
  const userAgent = navigator.userAgent;
  return !/Chrome/.test(userAgent) || /OPR|Edge/.test(userAgent);
};

const PageShell = styled(Box)({
  minHeight: '100vh',
  background: '#fff',
  paddingTop: '82px',
  '@media (max-width: 1000px)': {
    paddingTop: '12px',
  },
});

const DashboardContainer = styled(Container)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '196px minmax(0, 1fr)',
  gap: '28px',
  paddingBottom: '28px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
}));

const SidebarCard = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: '88px',
  alignSelf: 'start',
  borderRadius: 0,
  padding: '8px 0',
  background: 'transparent',
  boxShadow: 'none',
  '@media (max-width: 1000px)': {
    top: '12px',
  },
  [theme.breakpoints.down('md')]: {
    position: 'static',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    '& > .MuiTypography-root': { display: 'none' },
    '& > .MuiBox-root': { display: 'contents' },
    '& .MuiDivider-root': { display: 'none' },
    '& .MuiButton-root': { width: 'auto', margin: 0, fontSize: '12px', padding: '8px' },
    '& .MuiButton-startIcon': { display: 'none' },
  },
}));

const MainCard = styled(Paper)({
  borderRadius: '20px',
  padding: '18px',
  border: '1px solid rgba(153, 108, 254, 0.12)',
  boxShadow: '0 14px 34px rgba(41, 22, 89, 0.07)',
  background: 'rgba(255, 255, 255, 0.94)',
});

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: '6px',
  padding: '9px 11px',
  marginBottom: '6px',
  textTransform: 'none',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: active ? '#fff' : '#575c68',
  background: active ? '#8250df' : 'transparent',
  boxShadow: 'none',
  '&:hover': {
    background: active ? '#7040c7' : '#f3effa',
  },
}));

const GroupCard = styled(Paper)({
  borderRadius: '15px',
  padding: '14px',
  marginBottom: '12px',
  border: '1px solid #edf0f7',
  boxShadow: '0 6px 18px rgba(29, 22, 54, 0.045)',
});

const TestGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px',
  marginTop: '12px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const FullTestItem = styled(Box)({
  minWidth: 0,
});

const kindMap = {
  Cambridge: 'cambridge',
  'Practice Plus': 'practice plus',
  'Recent Actual Test': 'recent actual test',
};

const DASHBOARD_VIEWS = ['dashboard', 'results', 'sectional', 'full', 'community'];
const DASHBOARD_OFFER_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const DASHBOARD_OFFER_URGENCY_DAYS = 3;
const DASHBOARD_OFFER_URGENCY_HOURS = 24;

function getDashboardOfferStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-dashboard-offer-start-at-${userId}`;
}

function getDashboardOfferBarStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-dashboard-offer-bar-visible-${userId}`;
}

function formatDashboardOfferTime(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function getDashboardViewFromParams(searchParams) {
  const view = searchParams.get('view');
  return DASHBOARD_VIEWS.includes(view) ? view : 'dashboard';
}

function getKindFromParams(searchParams) {
  const kind = searchParams.get('kind');
  return testFilters.find((filter) => filter.value === kind)?.value || testFilters[0].value;
}

function getSectionFromParams(searchParams) {
  const section = searchParams.get('section');
  return sections.find((item) => item.title === section)?.title || sections[0].title;
}

function getGroupId(test) {
  return test?.groupId || test?.group_id || test?.groupID || null;
}

function getStoredGroupName(test) {
  return test?.group_name || test?.groupName || '';
}

function getRawTestKind(test) {
  return test?.test_kind || test?.testKind || test?.test_kind_name || test?.testKindName || '';
}

function titleCase(value) {
  return String(value || '')
    .replace(/[_-]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function usesShiftedGroupPrefix(kindLabel) {
  return kindLabel === 'Practice Plus' || kindLabel === 'Recent Actual Test';
}

function getGroupPrefix(groupId, kindLabel = '') {
  if (!groupId) return 'individual';
  const raw = String(groupId).trim();
  const digits = raw.replace(/\D/g, '');
  if (usesShiftedGroupPrefix(kindLabel) && digits.length >= 3) {
    return digits.slice(1, 3);
  }
  return (digits || raw).slice(0, 2);
}

function getKindLabel(test) {
  const explicitKind = getRawTestKind(test);
  if (explicitKind) {
    return normalizeIeltsTestKind(explicitKind);
  }

  const groupName = getStoredGroupName(test);
  const lowerName = groupName.toLowerCase();
  const compactName = lowerName.replace(/[^a-z0-9]/g, '');
  if (lowerName.includes('cambridge')) return 'Cambridge';
  if (lowerName.includes('idp') || compactName.includes('recentactualtest')) return 'Recent Actual Test';
  if (compactName.includes('britishcouncil') || compactName.includes('practiceplus')) return 'Practice Plus';

  const withoutNumbers = groupName.replace(/\d+/g, '').trim();
  return titleCase(withoutNumbers) || 'Group';
}

function isFreeCambridgeSectionalTest(test) {
  const category = String(test?.category_name || '').toLowerCase();
  const isSectionalCategory = ['listening', 'reading', 'writing', 'speaking'].includes(category);
  return getKindLabel(test) === 'Cambridge' && isSectionalCategory;
}

function getGroupKey(test, includeIndividual) {
  const rawGroupId = getGroupId(test);
  if (!rawGroupId && !includeIndividual) return null;
  if (!rawGroupId) return 'individual';
  const kindLabel = getKindLabel(test);
  const prefix = getGroupPrefix(rawGroupId, kindLabel);
  return `${kindLabel.toLowerCase()}-${prefix}`;
}

function getGroupDisplayName(groupKey, groupTests) {
  if (groupKey === 'individual') return 'Individual Tests';
  const first = groupTests[0] || {};
  const kindLabel = getKindLabel(first);
  return `${kindLabel} ${getGroupPrefix(getGroupId(first), kindLabel)}`;
}

function getFullTestName(test, seriesName) {
  return getStoredGroupName(test) || `${seriesName} - Test`;
}

function getFullTestSortNumber(fullTest) {
  const namedMatch = String(fullTest.groupName || '').match(/test\s*(\d+)/i);
  if (namedMatch) return Number(namedMatch[1]);

  const groupIdDigits = String(fullTest.groupId || '').replace(/\D/g, '');
  if (groupIdDigits.length > 2) return Number(groupIdDigits.slice(2)) || 0;
  return Number(groupIdDigits) || 0;
}

function getTestName(test) {
  return test?.test_name || test?.name || test?.title || `Test ${test?.id || ''}`;
}

function getTestTypeLabel(test) {
  if (test?.test_type === 'full' || test?.full_test_attempt_id) return 'Full Test';
  return test?.category_name || 'Sectional';
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString();
}

function formatTime(totalTime, fallback = '00:30m') {
  if (!totalTime) return fallback;
  if (totalTime >= 60) return `${Math.floor(totalTime / 60)}h:${String(totalTime % 60).padStart(2, '0')}m`;
  return `00:${String(totalTime).padStart(2, '0')}m`;
}

function formatResultScore(test) {
  const score = test?.band_score ?? test?.score ?? test?.totalMarksObtained ?? test?.total_marks_obtained;
  return formatIELTSBandScore(score);
}

function groupByPrefix(items, includeIndividual = false) {
  const groups = new Map();
  items.forEach((test) => {
    const key = getGroupKey(test, includeIndividual);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(test);
  });

  return Array.from(groups.entries()).map(([groupKey, groupTests]) => {
    const representative = groupTests[0] || {};
    const rawGroupIds = [...new Set(groupTests.map(getGroupId).filter(Boolean))];
    const primaryGroupId = getGroupId(representative) || rawGroupIds[0] || groupKey;
    const kindLabel = getKindLabel(representative);
    return {
      groupKey,
      groupId: primaryGroupId,
      groupPrefix: getGroupPrefix(primaryGroupId, kindLabel),
      rawGroupIds,
      groupName: getGroupDisplayName(groupKey, groupTests),
      tests: groupTests,
    };
  });
}

function groupFullTestsBySeries(items) {
  const seriesMap = new Map();

  items.forEach((test) => {
    const groupId = getGroupId(test);
    const seriesKey = getGroupKey(test, false);
    if (!groupId || !seriesKey) return;

    if (!seriesMap.has(seriesKey)) {
      const kindLabel = getKindLabel(test);
      seriesMap.set(seriesKey, {
        groupKey: seriesKey,
        groupPrefix: getGroupPrefix(groupId, kindLabel),
        groupName: getGroupDisplayName(seriesKey, [test]),
        fullTestsMap: new Map(),
      });
    }

    const series = seriesMap.get(seriesKey);
    const fullTestKey = String(groupId);
    if (!series.fullTestsMap.has(fullTestKey)) {
      series.fullTestsMap.set(fullTestKey, {
        groupKey: `${seriesKey}-${fullTestKey}`,
        groupId,
        groupName: getFullTestName(test, series.groupName),
        tests: [],
      });
    }
    series.fullTestsMap.get(fullTestKey).tests.push(test);
  });

  return Array.from(seriesMap.values()).map((series) => ({
    ...series,
    fullTests: Array.from(series.fullTestsMap.values()).sort(
      (a, b) => getFullTestSortNumber(b) - getFullTestSortNumber(a)
    ),
  }));
}

const IELTSDashboardV2 = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = getDashboardViewFromParams(searchParams);
  const previousView = useRef(activeView);
  const selectedKind = getKindFromParams(searchParams);
  const selectedSection = getSectionFromParams(searchParams);
  const setActiveView = (view, section = selectedSection) => {
    const next = new URLSearchParams(searchParams);
    if (view === 'dashboard') next.delete('view');
    else next.set('view', view);
    if (view !== 'results') next.delete('groupId');
    if (view === 'sectional' && section !== sections[0].title) next.set('section', section);
    else next.delete('section');
    if (next.toString() !== searchParams.toString()) setSearchParams(next);
  };
  const [open, setOpen] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [resultsError, setResultsError] = useState(false);
  const [error, setError] = useState(null);
  const [loadingTestId, setLoadingTestId] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(() => {
    const stored = localStorage.getItem('ielts-type');
    return stored ? stored.toLowerCase() : 'academic';
  });
  const [profileData, setProfileData] = useState({
    averageScore: '- / 9',
    targetScore: '- / 9',
    examDate: null,
    loading: true,
    error: null,
  });
  const [showTargetScoreModal, setShowTargetScoreModal] = useState(false);
  const [showExamDateModal, setShowExamDateModal] = useState(false);
  const [newTargetScore, setNewTargetScore] = useState('');
  const [newExamDate, setNewExamDate] = useState('');
  const [examNow, setExamNow] = useState(() => Date.now());
  const examDeadline = profileData.examDate
    ? new Date(`${String(profileData.examDate).slice(0, 10)}T00:00:00`).getTime()
    : NaN;
  const examSeconds = Number.isFinite(examDeadline) ? Math.max(0, Math.floor((examDeadline - examNow) / 1000)) : null;

  useEffect(() => {
    setExamNow(Date.now());
    if (!Number.isFinite(examDeadline) || examDeadline <= Date.now() || activeView !== 'dashboard') return;
    const timer = setInterval(() => {
      const now = Date.now();
      setExamNow(now);
      if (now >= examDeadline) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [examDeadline, activeView]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOfferBar, setShowOfferBar] = useState(false);
  const [offerStartTs, setOfferStartTs] = useState(null);
  const [offerNowTs, setOfferNowTs] = useState(() => Date.now());
  const [offerCodeCopied, setOfferCodeCopied] = useState(false);

  const userData = useStoredUserData() || {};
  const isSubscribed = userData?.IsSubscribed || false;
  const canSeeOffer = Number(userData?.PortalID) === 1 && !isSubscribed;

  useEffect(() => {
    if (!canSeeOffer) {
      setShowOfferModal(false);
      setShowOfferBar(false);
      setOfferStartTs(null);
      return undefined;
    }
    const storageKey = getDashboardOfferStorageKey();
    const storedStart = Number(localStorage.getItem(storageKey));
    const hasValidStoredStart = Number.isFinite(storedStart) && storedStart > 0;
    const startTs = hasValidStoredStart ? storedStart : Date.now();

    if (!hasValidStoredStart) {
      localStorage.setItem(storageKey, String(startTs));
    }

    setOfferStartTs(startTs);
    setOfferNowTs(Date.now());
    setShowOfferModal(true);
    setShowOfferBar(localStorage.getItem(getDashboardOfferBarStorageKey()) === '1');

    const timerId = setInterval(() => {
      setOfferNowTs(Date.now());
    }, 1000);

    return () => clearInterval(timerId);
  }, [canSeeOffer]);

  const offerRemainingMs = useMemo(() => {
    if (!offerStartTs) return DASHBOARD_OFFER_DURATION_MS;
    const expiryTime = offerStartTs + DASHBOARD_OFFER_DURATION_MS;
    return expiryTime - offerNowTs;
  }, [offerStartTs, offerNowTs]);

  const isOfferExpired = offerRemainingMs <= 0;
  const isOfferFinalHours = offerRemainingMs > 0 && offerRemainingMs <= DASHBOARD_OFFER_URGENCY_HOURS * 60 * 60 * 1000;
  const isOfferFinalDays = offerRemainingMs > 0 && offerRemainingMs <= DASHBOARD_OFFER_URGENCY_DAYS * 24 * 60 * 60 * 1000;
  const offerUrgencyLabel = isOfferFinalHours ? 'Final hours' : isOfferFinalDays ? 'Ending soon' : 'Limited offer';

  const handleDismissOfferModal = () => {
    setShowOfferModal(false);
    if (canSeeOffer && !isOfferExpired) {
      setShowOfferBar(true);
      localStorage.setItem(getDashboardOfferBarStorageKey(), '1');
    }
  };

  const handleCopyOfferCode = async () => {
    if (isOfferExpired) return;
    try {
      await navigator.clipboard.writeText('learnswift20');
      setOfferCodeCopied(true);
      setTimeout(() => setOfferCodeCopied(false), 1800);
    } catch (error) {
      setOfferCodeCopied(false);
    }
  };

  const handleOpenPlanInfo = () => {
    setShowOfferModal(false);
    setShowOfferBar(false);
    localStorage.removeItem(getDashboardOfferBarStorageKey());
    navigate('/plan-info');
  };

  const fetchProfileData = useCallback(async () => {
    try {
      setProfileData((prev) => ({ ...prev, loading: true, error: null }));
      const response = await fetch(`${Base_URL}/app/users/get-ielts-profile`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to fetch profile data');
      const profile = data.response;
      const averageScore = formatIELTSBandScore(profile.average_score);
      setProfileData({
        averageScore: averageScore !== '-' ? `${averageScore} / 9` : '- / 9',
        targetScore: profile.ielts_target_score ? `${profile.ielts_target_score} / 9` : '- / 9',
        examDate: profile.ielts_exam_date || null,
        loading: false,
        error: null,
      });
    } catch (err) {
      setProfileData((prev) => ({ ...prev, loading: false, error: err.message }));
    }
  }, []);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      const testsArr = Array.isArray(data) ? data : Array.isArray(data.response) ? data.response : [];
      setTests(testsArr);
    } catch (err) {
      setError('Failed to fetch IELTS tests');
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    setResultsError(false);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getUserTestsInfo`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok === false || (data.responseCode && data.responseCode !== 200)) throw new Error('Failed to fetch results');
      setResults(data.response?.tests || []);
    } catch (err) {
      setResults([]);
      setResultsError(true);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    setOpen(isNotChrome());
    const localUserData = JSON.parse(localStorage.getItem('userData') || '{}');
    setShowFooter((localUserData?.PortalId || 0) === 1);
    localStorage.setItem('exam-type', 'ielts');
    if (!localStorage.getItem('ielts-type')) localStorage.setItem('ielts-type', 'academic');
    window.dispatchEvent(new CustomEvent('examTypeChanged', { detail: { examType: 'ielts' } }));

    fetchTests();
    fetchProfileData();
    fetchResults();

    const handleIeltsTypeChange = (event) => {
      const { ieltsType } = event.detail || {};
      if (ieltsType) setSelectedTestType(ieltsType.toLowerCase());
    };
    window.addEventListener('ieltsTypeChanged', handleIeltsTypeChange);
    return () => window.removeEventListener('ieltsTypeChanged', handleIeltsTypeChange);
  }, [fetchTests, fetchProfileData, fetchResults]);

  useEffect(() => {
    const returningToDashboard = activeView === 'dashboard' && previousView.current !== 'dashboard';
    previousView.current = activeView;
    if (returningToDashboard) {
      fetchResults();
      fetchProfileData();
    }
  }, [activeView, fetchResults, fetchProfileData]);

  const sectionCounts = useMemo(() => {
    const counts = { Listening: 0, Reading: 0, Writing: 0, Speaking: 0 };
    tests.forEach((test) => {
      const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
      const matchesSelectedKind = getKindLabel(test).toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase());
      if (!matchesType || !matchesSelectedKind || !test.category_name) return;
      const category = test.category_name.toLowerCase();
      if (category.includes('listening')) counts.Listening += 1;
      else if (category.includes('reading')) counts.Reading += 1;
      else if (category.includes('writing')) counts.Writing += 1;
      else if (category.includes('speaking')) counts.Speaking += 1;
    });
    return counts;
  }, [tests, selectedKind, selectedTestType]);

  const matchesKind = useCallback((test) => {
    return getKindLabel(test).toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase());
  }, [selectedKind]);

  const matchesType = useCallback(
    (test) => (test.test_type ? test.test_type.toLowerCase() === selectedTestType : true),
    [selectedTestType]
  );

  const allFullMockSeries = useMemo(() => {
    const groupedSeries = groupFullTestsBySeries(
      tests.filter((test) => getGroupId(test) && matchesType(test))
    );

    return groupedSeries.map((series) => ({
      ...series,
      fullTests: series.fullTests.map((fullTest) => {
        const categories = [...new Set(fullTest.tests.map((test) => test.category_name))].filter(Boolean);
        const representative = fullTest.tests[0];
        const totalDuration = fullTest.tests.reduce(
          (sum, test) => sum + (
            test.category_name === 'Listening'
              ? IELTS_SECTION_DURATIONS.Listening
              : (test.total_time || 0)
          ),
          0
        );
        const speakingTest = fullTest.tests.find(
          (test) => String(test.category_name || '').toLowerCase() === 'speaking'
        );
        const requiredCategories = ['Listening', 'Reading', 'Writing'];
        const lowerCategories = categories.map((category) => category.toLowerCase());
        const missingCategories = requiredCategories.filter((category) => !lowerCategories.includes(category.toLowerCase()));

        return {
          ...fullTest,
          representative,
          categories,
          totalDuration,
          missingCategories,
          hasSpeakingSimulation: Boolean(speakingTest?.speaking_simulation_ready),
          hasAllRequiredCategories: missingCategories.length === 0,
          isUnlocked: isSubscribed,
          hasInProgress: fullTest.tests.some((test) => test.has_in_progress_full_attempt || test.resume_full_test_attempt_id),
          latestSummary: fullTest.tests.find((test) => test.latest_full_attempt_summary)?.latest_full_attempt_summary || null,
          resumeAttemptId: fullTest.tests.find((test) => test.resume_full_test_attempt_id)?.resume_full_test_attempt_id || null,
        };
      }).filter((fullTest) => fullTest.hasAllRequiredCategories),
    })).filter((series) => series.fullTests.length > 0);
  }, [tests, matchesType, isSubscribed]);

  const fullMockSeries = useMemo(() => allFullMockSeries.map((series) => ({
    ...series,
    fullTests: series.fullTests.filter((group) => matchesKind(group.representative)),
  })).filter((series) => series.fullTests.length > 0), [allFullMockSeries, matchesKind]);

  const sectionalGroups = useMemo(() => {
    const filtered = tests.filter((test) => {
      const sectionMatch = test.category_name?.toLowerCase() === selectedSection.toLowerCase();
      return sectionMatch && matchesKind(test) && matchesType(test);
    });
    return groupByPrefix(filtered, true).map((group) => ({
      ...group,
      tests: group.tests.map((test) => ({
        ...test,
        isUnlocked: isSubscribed || isFreeCambridgeSectionalTest(test),
      })),
    }));
  }, [tests, matchesKind, matchesType, selectedSection, isSubscribed]);

  const recentResults = useMemo(() => (results || []).slice(0, 5), [results]);

  const navigateToResultDetails = (result) => {
    if (result?.test_type === 'full' || result?.full_test_attempt_id || result?.fullTestAttemptId) {
      const fullTestAttemptId = result.full_test_attempt_id || result.fullTestAttemptId;
      if (fullTestAttemptId) {
        navigate(`/ielts-test-details?type=full&fullTestAttemptId=${fullTestAttemptId}`, { state: { resultsReturnTo: '/ielts-dashboard' } });
        return;
      }
    }

    const attemptId = result?.attempt_id || result?.attemptId;
    if (attemptId) {
      navigate(`/ielts-test-details?type=individual&attemptId=${attemptId}`, { state: { resultsReturnTo: '/ielts-dashboard' } });
      return;
    }

    setActiveView('results');
  };

  const getSectionIcon = (iconName) => {
    switch (iconName) {
      case 'listening': return <ListeningIcon sx={{ fontSize: 22 }} />;
      case 'reading': return <ReadingIcon sx={{ fontSize: 22 }} />;
      case 'writing': return <WritingIcon sx={{ fontSize: 22 }} />;
      case 'speaking': return <SpeakingIcon sx={{ fontSize: 22 }} />;
      default: return null;
    }
  };

  const resolveFullTestResumeAttemptId = (group) => Number(
    group.resumeAttemptId || group.latestSummary?.full_test_attempt_id || 0
  );

  const getFullTestDraftSummary = (group) => findFullIeltsDraftSummaryFromBrowser({
    groupId: group.groupId,
    tests: group.tests,
  });

  const resumableTests = useMemo(() => {
    const sectional = tests.filter((test) => matchesType(test)
      && (isSubscribed || isFreeCambridgeSectionalTest(test))).flatMap((test) => {
      const draft = loadIeltsDraftFromBrowser(buildIeltsDraftKey({ scope: 'sectional', section: test.category_name, testId: test.id }));
      return draft ? [{ test, name: getTestName(test), section: test.category_name, updatedAt: draft.updatedAt || draft.updated_at }] : [];
    });
    const full = allFullMockSeries.flatMap((series) => series.fullTests).filter((group) => group.isUnlocked).flatMap((group) => {
      const draft = findFullIeltsDraftSummaryFromBrowser({ groupId: group.groupId, tests: group.tests });
      return (group.resumeAttemptId || group.latestSummary?.full_test_attempt_id || draft?.fullTestAttemptId)
        && (group.hasInProgress || group.latestSummary?.status === 'in_progress' || draft)
        ? [{ group, name: group.groupName, section: 'Full test', updatedAt: draft?.updatedAt || group.latestSummary?.updated_at }]
        : [];
    });
    return [...sectional, ...full].sort((a, b) => (Date.parse(b.updatedAt) || 0) - (Date.parse(a.updatedAt) || 0)).slice(0, 3);
  }, [tests, matchesType, isSubscribed, allFullMockSeries]);

  const clearFullTestDrafts = async (group, fullTestAttemptId = null) => {
    const draftKeys = removeFullIeltsDraftsFromBrowser({
      groupId: group.groupId,
      tests: group.tests,
      fullTestAttemptId,
    });

    await Promise.allSettled(
      draftKeys.map((draftKey) => fetch(`${Base_URL}/app/users/ielts-tests/drafts`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftKey,
          scope: 'full',
          groupId: group.groupId,
          fullTestAttemptId,
        }),
      }))
    );
  };

  const handleFullTestResume = async (group) => {
    const draftSummary = getFullTestDraftSummary(group);
    const attemptId = resolveFullTestResumeAttemptId(group) || Number(draftSummary?.fullTestAttemptId || 0);
    if (!attemptId) {
      alert('No in-progress attempt to resume.');
      return;
    }
    const resumeMode = draftSummary?.mode === 'simulation' ? 'simulation' : 'practice';
    navigate(buildMockTestPath({ exam: 'ielts', scope: 'full', id: group.groupId, name: group.groupName }), {
      state: { requestedMode: resumeMode, requestedResumeAttemptId: Number(attemptId) },
    });
  };

  const handleFullTestStart = async (group, selectedMode = 'practice') => {
    const testMode = selectedMode === 'simulation' ? 'simulation' : 'practice';
    const loadingKey = `full-${group.groupKey}-start`;
    setLoadingTestId(loadingKey);
    try {
      await clearFullTestDrafts(group);
      navigate(buildMockTestPath({ exam: 'ielts', scope: 'full', id: group.groupId, name: group.groupName }), {
        state: { requestedMode: testMode, forceRestart: true },
      });
    } catch (err) {
      alert(err.message || 'Failed to start full test');
    } finally {
      setLoadingTestId(null);
    }
  };

  const handleStartTest = async (testId, mode = 'practice') => {
    const test = tests.find((item) => Number(item.id) === Number(testId));
    if (!test) return;
    navigate(buildMockTestPath({ exam: 'ielts', scope: 'sectional', id: test.id, name: test.test_name }), {
      state: { requestedMode: mode, resumeDraft: mode === 'resume' },
    });
  };

  const updateTargetScore = async () => {
    if (!newTargetScore) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ielts_target_score: parseFloat(newTargetScore) }),
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to update target score');
      await fetchProfileData();
      setShowTargetScoreModal(false);
      setNewTargetScore('');
    } catch (err) {
      alert('Failed to update target score. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const updateExamDate = async () => {
    if (!newExamDate) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ielts_exam_date: newExamDate }),
      });
      const data = await response.json();
      if (data.responseCode !== 200) throw new Error(data.message || 'Failed to update exam date');
      await fetchProfileData();
      setShowExamDateModal(false);
      setNewExamDate('');
    } catch (err) {
      alert('Failed to update exam date. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const renderSidebar = () => (
    <SidebarCard component="nav" aria-label="IELTS navigation">
      <Typography sx={{ px: 1.4, mb: 2, fontSize: 12, fontWeight: 700, color: '#777b86' }}>IELTS WORKSPACE</Typography>
      <NavButton startIcon={<DashboardOutlinedIcon />} active={activeView === 'dashboard'} onClick={() => setActiveView('dashboard')}>
        Dashboard
      </NavButton>
      <Box data-tour="ielts-dashboard-navigation-actions">
        <NavButton startIcon={<AssessmentOutlinedIcon />} active={activeView === 'results'} onClick={() => setActiveView('results')}>
          Results
        </NavButton>
        <Divider sx={{ my: 1 }} />
        <NavButton startIcon={<MenuBookOutlinedIcon />} active={activeView === 'sectional'} onClick={() => setActiveView('sectional')}>
          Sectional Mock Test
        </NavButton>
        <NavButton startIcon={<AssignmentOutlinedIcon />} active={activeView === 'full'} onClick={() => setActiveView('full')}>
          Full Mock Test
        </NavButton>
      </Box>
    </SidebarCard>
  );

  const renderFilterRow = () => (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {testFilters.map((filter) => (
        <Button
          key={filter.value}
          variant={selectedKind === filter.value ? 'contained' : 'outlined'}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            if (filter.value === testFilters[0].value) next.delete('kind');
            else next.set('kind', filter.value);
            setSearchParams(next);
          }}
          size="small"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: selectedKind === filter.value ? themeColors.primary : '#fff',
            borderColor: 'rgba(153,108,254,0.35)',
            color: selectedKind === filter.value ? '#fff' : '#4b3d72',
            '&:hover': {
              backgroundColor: selectedKind === filter.value ? themeColors.primaryHover : '#f7f1ff',
              borderColor: themeColors.primary,
            },
          }}
        >
          {filter.label}
        </Button>
      ))}
    </Stack>
  );

  const renderScoreCards = () => (
    <section className="ielts-overview-summary" aria-label="IELTS progress" data-tour="ielts-dashboard-scores">
      {[
        { title: 'Average band', value: profileData.averageScore === '- / 9' ? 'No results yet' : profileData.averageScore, icon: <CalculatorIcon />, color: 'teal' },
        { title: 'Target band', value: profileData.targetScore === '- / 9' ? 'Not set' : profileData.targetScore, icon: <TargetIcon />, color: 'purple', edit: () => setShowTargetScoreModal(true) },
        { title: 'Exam countdown', value: examSeconds === null ? 'No exam date' : (
          <span className="ielts-exam-countdown">
            {[
              ['Days', Math.floor(examSeconds / 86400)],
              ['Hours', Math.floor(examSeconds / 3600) % 24],
              ['Minutes', Math.floor(examSeconds / 60) % 60],
              ['Seconds', examSeconds % 60],
            ].map(([label, value]) => <span key={label}><span>{String(value).padStart(2, '0')}</span><small>{label}</small></span>)}
          </span>
        ), icon: <CalendarIcon />, color: 'rose', edit: () => setShowExamDateModal(true) },
      ].map((item) => (
        <div className={`ielts-overview-stat ${item.color}`} key={item.title}>
          <span className={`ielts-overview-icon ${item.color}`}>{item.icon}</span>
          <div>
            <span className="ielts-overview-label">{item.title}</span>
            <strong>{profileData.loading ? 'Loading...' : profileData.error ? 'Unavailable' : item.value}</strong>
          </div>
          {item.edit && (
            <Tooltip title={`Edit ${item.title.toLowerCase()}`}>
              <IconButton aria-label={`Edit ${item.title.toLowerCase()}`} size="small" onClick={item.edit}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ))}
      {profileData.error && <Button size="small" onClick={fetchProfileData}>Retry summary</Button>}
    </section>
  );

  const renderRecentResults = () => (
    <section className="ielts-overview-results" data-tour="ielts-dashboard-results" aria-labelledby="ielts-recent-results">
      <div className="ielts-overview-section-heading">
        <h2 id="ielts-recent-results">Recent results</h2>
        <Button onClick={() => setActiveView('results')} size="small" endIcon={<ArrowForwardIcon />}>View all</Button>
      </div>
      {resultsLoading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} aria-label="Loading results" /></Box>
      ) : resultsError ? (
        <div className="ielts-overview-empty" role="alert">
          <p>Recent results could not be loaded.</p>
          <Button onClick={fetchResults} size="small">Retry</Button>
        </div>
      ) : recentResults.length === 0 ? (
        <div className="ielts-overview-empty">
          <AssessmentOutlinedIcon />
          <h3>No IELTS results yet</h3>
          <Button onClick={() => setActiveView('sectional')} endIcon={<ArrowForwardIcon />}>Browse sectional tests</Button>
        </div>
      ) : (
        <div className="ielts-overview-result-list">
          {recentResults.map((result, index) => {
            const status = String(result.status || 'completed').replace(/_/g, ' ');
            const score = formatResultScore(result);
            return (
              <button
                type="button"
                className="ielts-overview-result"
                aria-label={`Review ${getTestName(result)}`}
                key={result.attempt_id || result.full_test_attempt_id || index}
                onClick={() => navigateToResultDetails(result)}
              >
                <span className="ielts-overview-result-icon">{getSectionIcon(String(result.category_name || '').toLowerCase()) || <AssignmentOutlinedIcon />}</span>
                <span className="ielts-overview-result-name">
                  <strong>{getTestName(result)}</strong>
                  <span>{getTestTypeLabel(result)}<span aria-hidden="true"> / </span>{formatDate(result.date_time)}</span>
                </span>
                <span className={`ielts-overview-status ${status === 'completed' ? 'complete' : ''}`}>{status}</span>
                <span className="ielts-overview-band"><strong>{score === '-' ? 'N/A' : score}</strong><small>Band</small></span>
                <ArrowForwardIcon className="ielts-overview-result-arrow" fontSize="small" />
              </button>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderDashboard = () => (
    <div className="ielts-overview">
      <header className="ielts-overview-heading" data-tour="ielts-dashboard-heading">
        <div>
          <span className="ielts-overview-eyebrow">A little progress, every day</span>
          <div data-tour="ielts-dashboard-heading-focus" className="ielts-overview-title">
            <h1>IELTS Dashboard</h1>
            <IeltsDashboardTour
              userId={userData?.UserId}
              ready={!loading && !resultsLoading && !profileData.loading && !open && !showOfferModal}
            />
          </div>
        </div>
        <span className="ielts-overview-exam-type">IELTS {selectedTestType === 'general' ? 'General Training' : 'Academic'}</span>
      </header>
      {renderScoreCards()}
      {resumableTests.length > 0 && (
        <section className="ielts-overview-resume" aria-label="Continue a test">
          <div className="ielts-overview-section-heading"><h2>Continue a test</h2><span>Saved progress</span></div>
          {resumableTests.map((item) => (
            <div className="ielts-overview-resume-row" key={item.group ? `full-${item.group.groupId}` : item.test.id}>
              <PlayArrowRoundedIcon />
              <div><strong>{item.name}</strong><span>{item.section}</span></div>
              <Button
                aria-label={`Resume ${item.name}`}
                endIcon={<ArrowForwardIcon />}
                onClick={() => item.group ? handleFullTestResume(item.group) : handleStartTest(item.test.id, 'resume')}
              >Resume</Button>
            </div>
          ))}
        </section>
      )}
      <div className="ielts-overview-study-grid">
        <section className="ielts-overview-practice" aria-labelledby="ielts-practice-heading">
          <div className="ielts-overview-section-heading"><h2 id="ielts-practice-heading">What will you practise today?</h2></div>
          <div className="ielts-overview-skills">
            {sections.map((section) => (
              <button
                className={`ielts-overview-skill ${section.icon}`}
                type="button"
                key={section.id}
                aria-label={`Practise ${section.title}`}
                onClick={() => setActiveView('sectional', section.title)}
              >
                <span className="ielts-overview-skill-icon">{getSectionIcon(section.icon)}</span>
                <strong>{section.title}</strong>
                <span>Sectional tests</span>
                <ArrowForwardIcon className="ielts-overview-skill-arrow" fontSize="small" />
              </button>
            ))}
          </div>
          <div className="ielts-overview-full-test">
            <AssignmentOutlinedIcon />
            <div><strong>Ready for a full mock?</strong><span>All sections, one test.</span></div>
            <Button onClick={() => setActiveView('full')} endIcon={<ArrowForwardIcon />}>Browse tests</Button>
          </div>
        </section>
        <section className="ielts-overview-planner" aria-labelledby="ielts-planner-heading">
          <div className="ielts-overview-planner-top"><AutoAwesomeIcon /><span className="ielts-overview-planned">Coming later</span></div>
          <h2 id="ielts-planner-heading">AI study planner</h2>
          <p>Your next chapter, planned around you.</p>
          <img className="ielts-overview-planner-image" src={studyPlannerImage} width="480" height="320" alt="" />
          <span className="ielts-overview-planner-note">Not available yet</span>
        </section>
      </div>
      <div className="ielts-overview-bottom">
        {renderRecentResults()}
        <section className="ielts-overview-community" data-tour="ielts-dashboard-community" aria-label="IELTS community">
          <CommunityChat userData={userData} compact />
        </section>
      </div>
    </div>
  );

  const renderFullTests = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#29213c', fontSize: 30, lineHeight: 1.4, mt: 1, mb: 3 }}>
        Full Mock Test
      </Typography>
      {renderFilterRow()}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : fullMockSeries.length === 0 ? (
        <Typography color="text.secondary">No full mock tests available.</Typography>
      ) : (
        fullMockSeries.map((series) => (
          <GroupCard className="ielts-sectional-group" key={`full-series-${series.groupKey}`}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={0.5}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 650, color: '#29213c' }}>{series.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {series.fullTests.length} full mock tests
                </Typography>
              </Box>
              <Chip label={`Group ${series.groupPrefix}`} size="small" sx={{ fontWeight: 800 }} />
            </Box>
            <TestGrid>
              {series.fullTests.map((group) => {
                const disabledMessage = group.hasAllRequiredCategories ? null : `Under development. ${group.missingCategories.join(', ')} sections are being added.`;
                const draftSummary = getFullTestDraftSummary(group);
                const shouldResume = Boolean(group.hasInProgress || String(group.latestSummary?.status || '').toLowerCase() === 'in_progress' || draftSummary);
                return (
                  <FullTestItem className="ielts-full-test-item" key={`full-${group.groupKey}`}>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {group.categories.map((category) => <Chip key={category} label={category} size="small" />)}
                    </Stack>
                    <TestCard
                      title={group.groupName}
                      time={formatTime(group.totalDuration, '2h:47m')}
                      difficulty={group.representative?.test_type || 'N/A'}
                      onStart={(selectedMode) => handleFullTestStart(group, selectedMode)}
                      onResume={() => handleFullTestResume(group)}
                      showResumeButton={shouldResume}
                      startLabel={shouldResume ? 'Restart' : 'Start'}
                      loadingStart={loadingTestId === `full-${group.groupKey}-start`}
                      loadingResume={loadingTestId === `full-${group.groupKey}-resume`}
                      isLocked={!group.isUnlocked}
                      disabledMessage={disabledMessage}
                      showSpeakingSimulationBadge={group.hasSpeakingSimulation}
                      sharePath={buildMockTestPath({ exam: 'ielts', scope: 'full', id: group.groupId, name: group.groupName })}
                    />
                  </FullTestItem>
                );
              })}
            </TestGrid>
          </GroupCard>
        ))
      )}
    </>
  );

  const renderSectionalTests = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#29213c', fontSize: 30, lineHeight: 1.4, mt: 1, mb: 3 }}>
        Sectional Mock Test
      </Typography>
      {renderFilterRow()}
      <div className="ielts-sectional-skills">
        {sections.map((section) => (
          <button
            type="button"
            key={section.id}
            className={`ielts-sectional-skill ${section.icon}`}
            aria-pressed={selectedSection === section.title}
            onClick={() => setActiveView('sectional', section.title)}
          >
            <span className="ielts-sectional-skill-icon">{getSectionIcon(section.icon)}</span>
            <span><strong>{section.title}</strong><small>{sectionCounts[section.title] || 0} tests</small></span>
          </button>
        ))}
      </div>
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : sectionalGroups.length === 0 ? (
        <Typography color="text.secondary">No sectional tests available.</Typography>
      ) : (
        sectionalGroups.map((group) => (
          <GroupCard className="ielts-sectional-group" key={`sectional-${group.groupKey}`}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" mb={1}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 650, color: '#29213c' }}>{group.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.tests.length} tests
                </Typography>
              </Box>
              <Chip label={selectedSection} className={`ielts-sectional-tag ${selectedSection.toLowerCase()}`} size="small" />
            </Box>
            <TestGrid>
              {group.tests.map((test) => {
                const testName = getTestName(test);
                return (
                  <SectionalTestCard
                    key={test.id}
                    title={testName}
                    time={formatTime(test.category_name === 'Listening' ? IELTS_SECTION_DURATIONS.Listening : test.total_time, selectedSection === 'Reading' || selectedSection === 'Writing' ? '01:00h' : selectedSection === 'Speaking' ? '00:15m' : '00:32m')}
                    difficulty={test.test_type || 'N/A'}
                    onStart={(mode) => handleStartTest(test.id, mode)}
                    selectedSection={selectedSection}
                    testId={test.id}
                    isLocked={!test.isUnlocked}
                    loading={loadingTestId === `sectional-${test.id}`}
                    showSimulationBadge={
                      selectedSection === 'Speaking'
                      && Boolean(test.speaking_simulation_ready)
                    }
                    sharePath={buildMockTestPath({ exam: 'ielts', scope: 'sectional', id: test.id, name: testName })}
                  />
                );
              })}
            </TestGrid>
          </GroupCard>
        ))
      )}
    </>
  );

  const renderTargetScoreModal = () => (
    <Dialog open={showTargetScoreModal} onClose={() => setShowTargetScoreModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', color: themeColors.primary, fontWeight: 800 }}>Update Target Score</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Target Score</InputLabel>
          <Select value={newTargetScore} label="Target Score" onChange={(event) => setNewTargetScore(event.target.value)}>
            {[4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0].map((score) => (
              <MenuItem key={score} value={score}>{score.toFixed(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={updateTargetScore} variant="contained" disabled={!newTargetScore || isUpdating}>Update</Button>
        <Button onClick={() => setShowTargetScoreModal(false)} disabled={isUpdating}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const renderExamDateModal = () => (
    <Dialog open={showExamDateModal} onClose={() => setShowExamDateModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', color: themeColors.primary, fontWeight: 800 }}>Update Exam Date</DialogTitle>
      <DialogContent>
        <TextField
          label="Exam Date"
          type="date"
          value={newExamDate}
          onChange={(event) => setNewExamDate(event.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={updateExamDate} variant="contained" disabled={!newExamDate || isUpdating}>Update</Button>
        <Button onClick={() => setShowExamDateModal(false)} disabled={isUpdating}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const renderDashboardOfferModal = () => (
    <Dialog open={showOfferModal && canSeeOffer} onClose={handleDismissOfferModal} maxWidth="sm" fullWidth>
      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 4 },
          background: 'linear-gradient(145deg, #fffaf3 0%, #f6f0ff 52%, #eef9ff 100%)',
        }}
      >
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: '20px',
            display: 'grid',
            placeItems: 'center',
            mb: 2,
            color: '#fff',
            background: 'linear-gradient(135deg, #996CFE 0%, #FF9F43 100%)',
            boxShadow: '0 14px 34px rgba(153, 108, 254, 0.28)',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 34 }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 900, color: '#20153d', mb: 0.8, fontSize: { xs: 26, sm: 32 } }}>
          IELTS Success Offer - 20% Off
        </Typography>

        <Typography sx={{ color: '#51476b', fontSize: 15.5, lineHeight: 1.65, mb: 2 }}>
          Upgrade today to accelerate your prep with premium IELTS tools and expert support.
        </Typography>

        {!isOfferExpired && (
          <Box
            sx={{
              mb: 2,
              p: 1.4,
              borderRadius: '12px',
              border: `1px solid ${isOfferFinalHours ? '#FECDD3' : isOfferFinalDays ? '#FDBA74' : '#D6BCFA'}`,
              backgroundColor: isOfferFinalHours ? '#FFF1F3' : isOfferFinalDays ? '#FFF7ED' : '#F4F0FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ color: isOfferFinalHours ? '#B42318' : isOfferFinalDays ? '#C2410C' : '#5B4A7A', fontWeight: 900, fontSize: 13.5 }}>
              {offerUrgencyLabel}: {formatDashboardOfferTime(offerRemainingMs)} left
            </Typography>
            <Typography sx={{ color: isOfferFinalHours ? '#B42318' : isOfferFinalDays ? '#C2410C' : '#5B4A7A', fontWeight: 700, fontSize: 12.5 }}>
              Offer auto-expires in your account
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            p: 1.8,
            mb: 2.2,
            borderRadius: '14px',
            border: '1px solid rgba(153,108,254,0.26)',
            background: 'rgba(255,255,255,0.84)',
            boxShadow: '0 10px 22px rgba(95, 62, 170, 0.12)',
          }}
        >
          <Chip
            label="Most Valuable Benefit"
            size="small"
            sx={{
              mb: 1,
              fontWeight: 800,
              color: '#fff',
              background: 'linear-gradient(135deg, #996CFE 0%, #7351E9 100%)',
            }}
          />
          <Typography sx={{ fontSize: { xs: 22, sm: 28 }, lineHeight: 1.2, color: '#23143f', fontWeight: 900 }}>
            1 Free 1-on-1 IELTS Class
          </Typography>
        </Box>

        <Stack spacing={1.1} sx={{ mb: 2.4 }}>
          {[
            'Unlimited AI checking',
            'Recent Actual Test + Practice Plus access',
            'Goal-focused preparation to help you achieve your target band',
          ].map((text) => (
            <Box key={text} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CheckCircleOutlineIcon sx={{ color: '#37965E', fontSize: 20 }} />
              <Typography sx={{ color: '#33294f', fontWeight: 700, fontSize: 14.5 }}>{text}</Typography>
            </Box>
          ))}
        </Stack>

        {!isOfferExpired && (
          <Box
            sx={{
              p: 2,
              borderRadius: '18px',
              border: `1px dashed ${isOfferFinalHours ? '#FDA4AF' : 'rgba(255, 159, 67, 0.8)'}`,
              background: isOfferFinalHours ? 'rgba(255, 241, 242, 0.95)' : 'rgba(255, 255, 255, 0.72)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferOutlinedIcon sx={{ color: isOfferFinalHours ? '#B42318' : '#FF8A00' }} />
              <Typography sx={{ color: '#4b3d72', fontWeight: 800 }}>
                Use code for 20% off
              </Typography>
            </Box>
            <Chip
              label={offerCodeCopied ? 'Code copied' : 'learnswift20'}
              onClick={handleCopyOfferCode}
              role="button"
              title="Click to copy discount code"
              sx={{
                fontWeight: 900,
                letterSpacing: 0.7,
                color: '#7a3d00',
                background: '#FFE0B2',
                border: '1px solid #FFB74D',
                cursor: 'pointer',
                '&:hover': {
                  background: '#FFD28A',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, pb: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleDismissOfferModal} sx={{ color: '#655b78', fontWeight: 800, textTransform: 'none' }}>
          Maybe later
        </Button>
        <Button
          onClick={handleOpenPlanInfo}
          variant="contained"
          sx={{
            borderRadius: '999px',
            px: 3,
            py: 1,
            fontWeight: 900,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
            boxShadow: '0 10px 22px rgba(153, 108, 254, 0.28)',
          }}
        >
          View subscription plans
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDashboardOfferBar = () => {
    if (!canSeeOffer || isOfferExpired || !showOfferBar) return null;
    return (
      <Box
        sx={{
          mb: 1.5,
          p: { xs: 1.3, sm: 1.6 },
          borderRadius: '16px',
          border: `1px solid ${isOfferFinalHours ? '#fb7185' : isOfferFinalDays ? '#f59e0b' : '#a78bfa'}`,
          background: isOfferFinalHours
            ? 'linear-gradient(135deg, #fff1f3 0%, #ffe4e8 42%, #ffe2e6 100%)'
            : isOfferFinalDays
              ? 'linear-gradient(135deg, #fff7ed 0%, #ffefdc 48%, #ffe8ce 100%)'
              : 'linear-gradient(135deg, #f6f1ff 0%, #eef5ff 52%, #e8fbff 100%)',
          boxShadow: isOfferFinalHours
            ? '0 14px 30px rgba(244, 63, 94, 0.18)'
            : '0 14px 30px rgba(124, 58, 237, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1.2,
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
          <AutoAwesomeIcon sx={{ color: isOfferFinalHours ? '#e11d48' : '#7c3aed', fontSize: 22 }} />
          <Box>
            <Typography sx={{ color: '#201238', fontWeight: 950, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.1 }}>
              1 Free 1-on-1 Class
            </Typography>
            <Typography sx={{ color: '#5b3d9a', fontWeight: 900, fontSize: 12.8 }}>
              Plus 20% off with code learnswift20
            </Typography>
            <Typography sx={{ color: '#564b70', fontWeight: 700, fontSize: 12.2 }}>
              {offerUrgencyLabel}: {formatDashboardOfferTime(offerRemainingMs)} left
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ ml: 'auto' }}>
          <Chip
            label={offerCodeCopied ? 'Code copied' : 'learnswift20'}
            onClick={handleCopyOfferCode}
            sx={{
              fontWeight: 900,
              cursor: 'pointer',
              color: '#7a3d00',
              background: '#FFE0B2',
              border: '1px solid #FFB74D',
              '&:hover': { background: '#FFD28A' },
            }}
          />
          <Button
            onClick={() => setShowOfferModal(true)}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 800, color: '#5e3ec8' }}
          >
            View offer
          </Button>
          <Button
            onClick={handleOpenPlanInfo}
            size="small"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              borderRadius: '999px',
              background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
            }}
          >
            View plans
          </Button>
          <IconButton
            size="small"
            onClick={() => {
              setShowOfferBar(false);
              localStorage.removeItem(getDashboardOfferBarStorageKey());
            }}
            sx={{ color: '#6b5a8f', border: '1px solid rgba(91, 74, 122, 0.2)' }}
            title="Hide offer bar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      <BrowserNotSupported open={open} close={setOpen} />
      <Navbar />
      <PageShell>
        <DashboardContainer maxWidth="xl">
          {renderSidebar()}
          <MainCard className={['sectional', 'full'].includes(activeView) ? 'ielts-sectional-catalog' : undefined} sx={['dashboard', 'results', 'sectional', 'full'].includes(activeView) ? { minWidth: 0, p: 0, border: 0, borderRadius: 0, boxShadow: 'none', background: 'transparent' } : { minWidth: 0 }}>
            {renderDashboardOfferBar()}
            {activeView === 'dashboard' && renderDashboard()}
            {activeView === 'results' && <IELTSResults embedded />}
            {activeView === 'sectional' && renderSectionalTests()}
            {activeView === 'full' && renderFullTests()}
          </MainCard>
        </DashboardContainer>
      </PageShell>
      {showFooter && (
        <Box sx={{ padding: '0rem 3% 0rem', background: 'white' }}>
          <Footer />
        </Box>
      )}
      {renderTargetScoreModal()}
      {renderExamDateModal()}
      {renderDashboardOfferModal()}
    </Box>
  );
};

export default IELTSDashboardV2;
